// Почему в ведомостях списка нет денег: одна и та же причина называется ОДИН раз.
//
// ## Откуда это взялось
//
// Независимая проверка кабинета 01.10.2026 по настоящим данным: в списке из 64 ведомостей фраза
// «табеля за этот месяц нет вовсе, часов взять неоткуда» стояла в 53 строках, а «на объект в этом
// месяце никого не назначили» ещё в 8. Человек читает повтор как разные утверждения и ищет между
// ними разницу, которой нет, а шесть строк из десяти на экране заняты одним и тем же. К тому же
// длинная фраза под подписью строки обрезалась меткой состояния («часов вз…»).
//
// ## Правило
//
// Причину пишет СЕРВЕР, а в строку она приезжает готовой фразой (`zero_reason`). Если одна и та же
// фраза у двух ведомостей и больше, она называется один раз, над списком, со счётом («У 53
// ведомостей за этот месяц нет табеля»), а в строках остаётся короткая пометка или ничего. Если
// причина у ведомости одна, она стоит в её строке целиком и переносится, а не обрезается.
//
// Порог два, а не три: сквозная проверка «один факт один раз» краснеет на любой фразе длиннее
// пяти слов, повторённой на экране дважды.
//
// ## Что не группируется
//
// Фразы с числом внутри («нет тарифа: 12 строк», «нет часов: 4 строки»). Число в них про ЭТУ
// ведомость: у соседней их 4, у третьей 30, и «у 20 ведомостей нет тарифа: 4 строки» прочиталось
// бы как «у каждой по четыре», чего никто не говорил. Такая причина остаётся в своей строке.
//
// ## Что знает этот файл о словах сервера
//
// Три дословные фразы (`server/app/services/payroll.py`, `_почему_ноль`) и то, как их сказать
// по-человечески. Фраза, которой здесь нет, не ломает ничего: она группируется так же, а над
// списком стоит её собственный текст.

import type { PayrollOut } from '@/shared/api/endpoints/payroll';
import { countLabel } from '@/shared/text/plural';

/** Сколько ведомостей с одной причиной делают её общей. */
const МИНИМУМ_ДЛЯ_ОБЩЕЙ = 2;

const ВЕДОМОСТЕЙ_У = ['ведомости', 'ведомостей', 'ведомостей'] as const;
const ОБЪЕКТОВ_НА = ['объект', 'объекта', 'объектов'] as const;

interface Описание {
  /** Строка над списком, когда причина общая. */
  сводка: (сколько: number) => string;
  /**
   * Короткая пометка в строке, когда причина общая. `null`: строка и так говорит то же самое
   * («считать некого», «ещё не считали»), и вторая пометка была бы повтором внутри строки.
   */
  пометка: string | null;
  /** Причина в самой строке, когда она у одной ведомости. `null`: строке нечего добавить. */
  строка: string | null;
}

const ИЗВЕСТНЫЕ: Readonly<Record<string, Описание>> = {
  'на объект в этом месяце никого не назначили': {
    сводка: (сколько) =>
      `На ${countLabel(сколько, ОБЪЕКТОВ_НА)} в этом месяце никого не назначили: считать некого.`,
    пометка: null,
    строка: 'на объект в этом месяце никого не назначили',
  },
  // Длинное тире сервера записано кодом: это ключ для сверки, а не текст для человека.
  'табеля за этот месяц нет вовсе \u2014 часов взять неоткуда': {
    сводка: (сколько) =>
      `У ${countLabel(сколько, ВЕДОМОСТЕЙ_У)} за этот месяц нет табеля: часов взять неоткуда.`,
    пометка: 'нет табеля',
    строка: 'нет табеля: часов взять неоткуда',
  },
  'ещё не считали: суммы не нулевые, а неизвестные': {
    сводка: (сколько) =>
      `У ${countLabel(сколько, ВЕДОМОСТЕЙ_У)} всё для расчёта есть, но его ещё не проводили.`,
    пометка: null,
    строка: null,
  },
};

/** Причина, названная над списком. */
export interface ОбщаяПричина {
  /** Фраза сервера дословно: по ней строки узнают свою причину. */
  текст: string;
  /** Сколько ведомостей списка с этой причиной. */
  сколько: number;
  /** Что написать над списком. */
  сводка: string;
}

/** Что дописать в строку ведомости. */
export interface ПричинаВСтроке {
  /** Коротко, в подписи строки: причина общая и названа над списком. */
  пометка: string | null;
  /** Целиком, второй линией под подписью: причина у этой ведомости одна. */
  полностью: string | null;
}

export interface РазборПричин {
  /** Общие причины: по убыванию числа ведомостей. Пусто, когда повторов нет. */
  общие: readonly ОбщаяПричина[];
  /** Что написать в строке этой ведомости. */
  дляСтроки: (ведомость: PayrollOut) => ПричинаВСтроке;
}

function причинаСервера(ведомость: PayrollOut): string | null {
  const текст = ведомость.zero_reason;
  return текст === null || текст === undefined || текст === '' ? null : текст;
}

/** Число внутри фразы означает, что она про эту ведомость, а не про многие. */
function причинаПроОднуВедомость(текст: string): boolean {
  return /\d/.test(текст);
}

function сводкаОбщей(текст: string, сколько: number): string {
  const известная = ИЗВЕСТНЫЕ[текст];
  if (известная !== undefined) {
    return известная.сводка(сколько);
  }
  // Незнакомая фраза: называем её как есть, с числом ведомостей. Лучше сказать словами сервера,
  // чем промолчать о повторе.
  return `У ${countLabel(сколько, ВЕДОМОСТЕЙ_У)}: ${текст}.`;
}

/**
 * Разобрать причины ведомостей, которые сейчас в списке.
 *
 * Считается по ПОКАЗАННЫМ строкам, а не по всему месяцу: над списком из двух ведомостей не
 * должно стоять «у 53», а когда поиск оставил по одной ведомости, каждая несёт свою причину.
 */
export function разобратьПричины(ведомости: readonly PayrollOut[]): РазборПричин {
  const счёт = new Map<string, number>();
  for (const ведомость of ведомости) {
    const текст = причинаСервера(ведомость);
    if (текст !== null && !причинаПроОднуВедомость(текст)) {
      счёт.set(текст, (счёт.get(текст) ?? 0) + 1);
    }
  }

  const общие: ОбщаяПричина[] = [...счёт.entries()]
    .filter(([, сколько]) => сколько >= МИНИМУМ_ДЛЯ_ОБЩЕЙ)
    .sort(([а, числоА], [б, числоБ]) => числоБ - числоА || а.localeCompare(б, 'ru'))
    .map(([текст, сколько]) => ({ текст, сколько, сводка: сводкаОбщей(текст, сколько) }));
  const названные = new Set(общие.map((причина) => причина.текст));

  return {
    общие,
    дляСтроки: (ведомость) => {
      const текст = причинаСервера(ведомость);
      if (текст === null) {
        return { пометка: null, полностью: null };
      }
      const известная = ИЗВЕСТНЫЕ[текст];
      if (названные.has(текст)) {
        return { пометка: известная?.пометка ?? null, полностью: null };
      }
      return { пометка: null, полностью: известная === undefined ? текст : известная.строка };
    },
  };
}
